import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFileDownloadUrl } from "../api/firebase/firestoreService";
import { CmmDocType } from "../enums/cmmDocTypes";
import { fetchSingleOrderDocs } from "../redux/slice/nycbsPharmaExternal/nycbsDocumentsSlice";
import { AppDispatch, RootState } from "../redux/store/store";

interface UseFetchPdfUrlOptions {
  orderId?: string;
  status?: string;
  shouldFetch?: boolean;
}

interface UseFetchPdfUrlReturn {
  pdfUrl: string;
  isPdfLoading: boolean;
  error: string | null;
}

export const useFetchPdfUrl = ({
  orderId,
  status = "Approved",
  shouldFetch = true,
}: UseFetchPdfUrlOptions): UseFetchPdfUrlReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { data: singleOrderDocs, loading: singleOrderDocsLoading } =
    useSelector((state: RootState) => state.nycbsDocuments);

  const getLatestDocDownloadUrlOfRequiredDocType = async (
    docType: CmmDocType,
  ) => {
    try {
      const docs = singleOrderDocs?.filter(
        (doc) => doc.document_type === docType,
      );

      if (docs && docs.length > 0) {
        const latestDoc = docs.sort(
          (a, b) =>
            new Date(b?.created_at ?? "").getTime() -
            new Date(a?.created_at ?? "").getTime(),
        )[0];

        const url = await getFileDownloadUrl(latestDoc?.file_path ?? "");
        return url;
      }

      return null;
    } catch (err) {
      console.error("Error fetching document URL:", err);
      return null;
    }
  };

  // Fetch single order documents when orderId is provided
  useEffect(() => {
    if (orderId && shouldFetch) {
      setIsPdfLoading(true);
      setError(null);
      dispatch(fetchSingleOrderDocs(orderId));
    }
  }, [orderId, shouldFetch, dispatch]);

  // Fetch PDF URL after documents are loaded
  useEffect(() => {
    const fetchUrl = async () => {
      if (singleOrderDocs && singleOrderDocs.length > 0) {
        // First, try to fetch based on the provided status
        const primaryDocType =
          status.toLowerCase() === "denied"
            ? CmmDocType.DENIAL_LETTER
            : CmmDocType.APPROVAL_LETTER;

        let url =
          await getLatestDocDownloadUrlOfRequiredDocType(primaryDocType);

        // If not found, try the other document type
        if (!url) {
          const alternateDocType =
            primaryDocType === CmmDocType.APPROVAL_LETTER
              ? CmmDocType.DENIAL_LETTER
              : CmmDocType.APPROVAL_LETTER;
          url =
            await getLatestDocDownloadUrlOfRequiredDocType(alternateDocType);
        }

        if (url) {
          setPdfUrl(url);
          setError(null);
        } else {
          setPdfUrl("");
          setError("No valid document available");
        }
        setIsPdfLoading(false);
      }
    };

    if (
      !singleOrderDocsLoading &&
      singleOrderDocs &&
      singleOrderDocs.length > 0 &&
      shouldFetch
    ) {
      fetchUrl();
    } else if (!singleOrderDocsLoading && shouldFetch) {
      setIsPdfLoading(false);
      if (singleOrderDocs && singleOrderDocs.length === 0) {
        setError("No documents found");
      }
    }
  }, [status, singleOrderDocsLoading, singleOrderDocs, shouldFetch]);

  return {
    pdfUrl,
    isPdfLoading: isPdfLoading || singleOrderDocsLoading,
    error,
  };
};
